import { computed, ref } from 'vue';
import {
  employmentReferenceApi,
  fetchEmploymentReferenceOptions,
  invalidateEmploymentReferenceOptions,
} from '@/api/employmentReferenceApi';
import { EMPLOYMENT_TYPE_OPTIONS, COMPANY_OPTIONS } from '@/constants/employmentOptions';

/** @type {import('vue').Ref<{ employment_types: Array, companies: Array }|null>} */
const snapshotRef = ref(null);
const loadingRef = ref(false);
const errorRef = ref('');

/** Clear in-memory catalog so employee forms reload on next ensureLoaded(). */
export function resetEmploymentReferenceCatalogSnapshot() {
  snapshotRef.value = null;
}

function fallbackSnapshot() {
  return {
    employment_types: EMPLOYMENT_TYPE_OPTIONS.map((row, index) => ({
      id: null,
      code: row.value,
      label: row.label,
      value: row.value,
      is_active: true,
      is_system: true,
      sort_order: index * 10,
    })),
    companies: COMPANY_OPTIONS.map((row, index) => ({
      id: null,
      name: row.value,
      label: row.label,
      value: row.value,
      employment_type_codes: [...(row.employmentTypes || [])],
      is_active: true,
      is_system: true,
      sort_order: index * 10,
    })),
  };
}

function normalizeCode(value) {
  return String(value ?? '').trim();
}

function findTypeByCode(code) {
  const key = normalizeCode(code);
  if (!key || !snapshotRef.value) return null;
  return (snapshotRef.value.employment_types || []).find((row) => row.code === key) || null;
}

function findCompanyByName(name) {
  const key = normalizeCode(name);
  if (!key || !snapshotRef.value) return null;
  return (snapshotRef.value.companies || []).find((row) => row.name === key) || null;
}

function mapTypeOption(row) {
  return {
    id: row.id ?? null,
    label: row.label || row.code,
    value: row.code,
    code: row.code,
    is_system: !!row.is_system,
  };
}

function mapCompanyOption(row) {
  return {
    id: row.id ?? null,
    label: row.name,
    value: row.name,
    name: row.name,
    employment_type_codes: row.employment_type_codes || [],
    is_system: !!row.is_system,
  };
}

export function getCatalogEmploymentTypeOptions() {
  const rows = snapshotRef.value?.employment_types || fallbackSnapshot().employment_types;
  return rows.filter((row) => row.is_active !== false).map(mapTypeOption);
}

export function getCatalogCompanyOptionsForEmploymentType(type) {
  const typeCode = normalizeCode(type);
  if (!typeCode) return [];
  const rows = snapshotRef.value?.companies || fallbackSnapshot().companies;
  return rows
    .filter(
      (row) =>
        row.is_active !== false &&
        (row.employment_type_codes || []).includes(typeCode)
    )
    .map(mapCompanyOption);
}

/**
 * API-backed employment types + employing companies catalog.
 */
export function useEmploymentReferenceCatalog() {
  const employmentTypes = computed(
    () => snapshotRef.value?.employment_types || fallbackSnapshot().employment_types
  );
  const companies = computed(
    () => snapshotRef.value?.companies || fallbackSnapshot().companies
  );
  const loading = computed(() => loadingRef.value);
  const error = computed(() => errorRef.value);

  async function ensureLoaded({ force = false } = {}) {
    if (!force && snapshotRef.value) return snapshotRef.value;
    loadingRef.value = true;
    errorRef.value = '';
    try {
      const data = await fetchEmploymentReferenceOptions({ force });
      snapshotRef.value = data;
      return data;
    } catch (err) {
      errorRef.value = err?.message || 'Failed to load employment reference options';
      return fallbackSnapshot();
    } finally {
      loadingRef.value = false;
    }
  }

  async function refresh() {
    invalidateEmploymentReferenceOptions();
    return ensureLoaded({ force: true });
  }

  async function createType(name) {
    const code = normalizeCode(name);
    if (!code) throw new Error('Employment type is required');
    const { type } = await employmentReferenceApi.types.create({ code, label: code });
    await refresh();
    return mapTypeOption(type);
  }

  async function updateType(currentValue, name) {
    const existing = findTypeByCode(currentValue);
    if (!existing?.id) throw new Error('Employment type not found');
    const code = normalizeCode(name);
    if (!code) throw new Error('Employment type is required');
    const { type } = await employmentReferenceApi.types.update({
      id: existing.id,
      code,
      label: code,
    });
    await refresh();
    return { ...mapTypeOption(type), previousValue: existing.code };
  }

  async function deleteType(value) {
    const existing = findTypeByCode(value);
    if (!existing?.id) throw new Error('Employment type not found');
    await employmentReferenceApi.types.delete({ id: existing.id });
    await refresh();
    return existing.code;
  }

  async function createCompany(name, employmentType) {
    const label = normalizeCode(name);
    const typeCode = normalizeCode(employmentType);
    if (!label) throw new Error('Company name is required');
    if (!typeCode) throw new Error('Select employment type first');
    const { company } = await employmentReferenceApi.companies.create({
      name: label,
      employment_type: typeCode,
    });
    await refresh();
    return mapCompanyOption(company);
  }

  async function updateCompany(currentValue, name, employmentType) {
    const existing = findCompanyByName(currentValue);
    if (!existing?.id) throw new Error('Company not found');
    const label = normalizeCode(name);
    const typeCode = normalizeCode(employmentType);
    if (!label) throw new Error('Company name is required');
    if (!typeCode) throw new Error('Select employment type first');
    const { company } = await employmentReferenceApi.companies.update({
      id: existing.id,
      name: label,
      employment_type: typeCode,
    });
    await refresh();
    return {
      ...mapCompanyOption(company),
      previousValue: existing.name,
    };
  }

  async function deleteCompany(value) {
    const existing = findCompanyByName(value);
    if (!existing?.id) throw new Error('Company not found');
    await employmentReferenceApi.companies.delete({ id: existing.id });
    await refresh();
    return existing.name;
  }

  function isManagedType(value) {
    return Boolean(findTypeByCode(value)?.id);
  }

  function isManagedCompany(value) {
    return Boolean(findCompanyByName(value)?.id);
  }

  return {
    employmentTypes,
    companies,
    loading,
    error,
    ensureLoaded,
    refresh,
    createType,
    updateType,
    deleteType,
    createCompany,
    updateCompany,
    deleteCompany,
    isManagedType,
    isManagedCompany,
    getTypeOptions: getCatalogEmploymentTypeOptions,
    getCompanyOptionsForType: getCatalogCompanyOptionsForEmploymentType,
  };
}
